import { useEffect, useState } from "react";
import { api, apiFetch, fmtMinutes } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, FileText, FileSpreadsheet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { toast } from "sonner";

const CHART_COLORS = ["#F97316", "#3B82F6", "#EAB308", "#16A34A", "#DC2626"];

export default function Reports() {
  const [kpis, setKpis] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/dashboard/kpis").then((r) => setKpis(r.data));
    api.get("/os").then((r) => setItems(r.data));
  }, []);

  const exportCsv = async () => {
    const res = await apiFetch("/reports/os.csv");
    const blob = await res.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "os_relatorio.csv";
    link.click();
    toast.success("Relatório CSV baixado");
  };

  const exportPdf = () => {
    window.print();
    toast.info("Use 'Salvar como PDF' na janela de impressão");
  };

  if (!kpis) return <div className="text-muted-foreground">Carregando...</div>;

  const summary = [
    { label: "Total de OS", value: kpis.total },
    { label: "Concluídas", value: kpis.done },
    { label: "Abertas", value: kpis.open },
    { label: "Em Andamento", value: kpis.in_progress },
    { label: "MTTR Médio", value: fmtMinutes(kpis.mttr_minutes) },
    { label: "MTBF Estimado", value: fmtMinutes(kpis.mtbf_minutes) },
    { label: "Downtime Total", value: fmtMinutes(kpis.total_downtime_minutes) },
    { label: "Canceladas", value: kpis.cancelled },
  ];

  return (
    <div className="space-y-6" data-testid="reports">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Análise Gerencial</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Relatórios</h1>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportCsv} variant="outline" className="rounded-sm gap-2" data-testid="export-csv">
            <FileSpreadsheet className="w-4 h-4" /> Exportar CSV/Excel
          </Button>
          <Button onClick={exportPdf} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="export-pdf">
            <FileText className="w-4 h-4" /> Exportar PDF
          </Button>
        </div>
      </div>

      {/* Summary */}
      <Card className="rounded-sm shadow-none p-6">
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground mb-4">Resumo Executivo</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {summary.map((s) => (
            <div key={s.label}>
              <div className="text-xs uppercase text-muted-foreground tracking-wider">{s.label}</div>
              <div className="font-heading font-black text-2xl mt-1">{s.value}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Chart by sector */}
      <Card className="rounded-sm shadow-none p-6">
        <h3 className="font-heading text-xl font-bold mb-4">OS por Setor</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={kpis.by_sector}>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
            <XAxis dataKey="name" fontSize={12} />
            <YAxis fontSize={12} allowDecimals={false} />
            <Tooltip contentStyle={{ backgroundColor: "#0F172A", border: "none", color: "#fff", borderRadius: 4 }} />
            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
              {kpis.by_sector.map((entry) => <Cell key={entry.name} fill={CHART_COLORS[kpis.by_sector.indexOf(entry) % CHART_COLORS.length]} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Full history table */}
      <Card className="rounded-sm shadow-none overflow-hidden">
        <div className="p-5 border-b">
          <h3 className="font-heading text-xl font-bold">Histórico Completo de OS</h3>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>OS</TableHead>
              <TableHead>Equipamento</TableHead>
              <TableHead>Setor</TableHead>
              <TableHead>Disciplina</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Downtime</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Nenhuma OS registrada</TableCell></TableRow>
            ) : items.map((o) => (
              <TableRow key={o.id}>
                <TableCell className="font-mono-tag">#{o.number}</TableCell>
                <TableCell>{o.equipment_name} <span className="font-mono-tag text-xs text-muted-foreground">[{o.equipment_tag}]</span></TableCell>
                <TableCell>{o.sector}</TableCell>
                <TableCell>
                  <span className={
                    o.discipline === "Elétrica" ? "inline-flex items-center gap-1.5 text-blue-700 dark:text-blue-400" :
                    o.discipline === "Automação" ? "inline-flex items-center gap-1.5 text-purple-700 dark:text-purple-400" :
                    o.discipline === "Lubrificação" ? "inline-flex items-center gap-1.5 text-yellow-700 dark:text-yellow-400" :
                    "inline-flex items-center gap-1.5 text-orange-700 dark:text-orange-400"
                  }>
                    <span className={
                      "w-1.5 h-1.5 rounded-full " + (
                        o.discipline === "Elétrica" ? "bg-blue-500" :
                        o.discipline === "Automação" ? "bg-purple-500" :
                        o.discipline === "Lubrificação" ? "bg-yellow-500" :
                        "bg-orange-500"
                      )
                    } />
                    {o.discipline}
                  </span>
                </TableCell>
                <TableCell>{o.maintenance_type}</TableCell>
                <TableCell>{o.status}</TableCell>
                <TableCell>{fmtMinutes(o.downtime_minutes)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
